// CustomPagination.tsx
import React, {useEffect, useRef} from 'react';
import {Pagination, PaginationItem} from '@mui/material';
import {useResponsive} from '../../contexts/ResponsiveContext';

interface CustomPaginationActionsProps {
    count: number;
    page: number; // 0-based
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;

    autoScrollTop?: boolean;
    scrollAnchorRef?: React.RefObject<HTMLElement>;
    scrollBehavior?: ScrollBehavior; // 'auto' | 'smooth'
    loading?: boolean;
    scrollOffset?: number;
    /** 如果列表是在某個可捲動容器內，傳它的 ref（可選） */
    scrollContainerRef?: React.RefObject<HTMLElement>;
}

const CustomPagination: React.FC<CustomPaginationActionsProps> = ({
                                                                      count,
                                                                      page,
                                                                      rowsPerPage,
                                                                      onPageChange,
                                                                      autoScrollTop = false,
                                                                      scrollAnchorRef,
                                                                      scrollBehavior = 'smooth',
                                                                      loading = false,
                                                                      scrollOffset = 0,
                                                                      scrollContainerRef,
                                                                  }) => {
    const { isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();
    const isMobile = isScreenSm || isScreenXs;
    const responsiveTabMargin = '0px -10px 0px -10px';

    const pendingScrollRef = useRef(false);

    // 找最近的可捲動容器（若沒傳入 scrollContainerRef）
    const getScrollableContainer = (el: HTMLElement | null): HTMLElement | Window => {
        if (scrollContainerRef?.current) return scrollContainerRef.current;
        let node: HTMLElement | null = el;
        while (node && node.parentElement) {
            const style = window.getComputedStyle(node);
            const overflowY = style.overflowY;
            const canScroll =
                (overflowY === 'auto' || overflowY === 'scroll') &&
                node.scrollHeight > node.clientHeight;
            if (canScroll) return node;
            node = node.parentElement;
        }
        return window;
    };

    const doScrollToAnchor = () => {
        const anchor = scrollAnchorRef?.current;
        if (!anchor) return;

        const container = getScrollableContainer(anchor);

        if (container === window) {
            const y = anchor.getBoundingClientRect().top + window.pageYOffset - (scrollOffset ?? 0);
            window.scrollTo({ top: y, behavior: scrollBehavior });
        } else {
            const c = container as HTMLElement;
            const anchorTop = anchor.getBoundingClientRect().top;
            const containerTop = c.getBoundingClientRect().top;
            const target = c.scrollTop + (anchorTop - containerTop) - (scrollOffset ?? 0);
            c.scrollTo({ top: target, behavior: scrollBehavior });
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(event as React.MouseEvent<HTMLButtonElement>, value - 1);

        if (autoScrollTop) {
            if (!loading) {
                setTimeout(() => {
                    doScrollToAnchor();
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => doScrollToAnchor());
                    });
                }, 400);
            } else {
                pendingScrollRef.current = true;
            }
        }
    };

    // loading -> false 時執行捲動（含重試一次，避免圖片載入晚一步）
    useEffect(() => {
        if (!loading && autoScrollTop && pendingScrollRef.current) {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    doScrollToAnchor();                // 第一次
                    requestAnimationFrame(() => {
                        doScrollToAnchor();              // 第二次，等下一個 frame
                        setTimeout(() => doScrollToAnchor(), 120); // 第三次，保險（圖片/字型載入）
                    });
                });
            }, 500); // 同樣延遲 500ms
            pendingScrollRef.current = false;
        }
    }, [loading, autoScrollTop]); // 只依賴 loading 與開關

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'white',
                width: isMobile ? responsiveWidth : undefined,
                margin: isScreenMd || isScreenSm || isScreenXs ? 'auto' : undefined,
                whiteSpace: 'nowrap',
            }}
            data-testid="pagination-page"
        >
            <Pagination
                count={Math.ceil(count / rowsPerPage)}
                page={page + 1}
                onChange={handlePageChange}
                shape="rounded"
                variant="outlined"
                showFirstButton={false}
                showLastButton={false}
                siblingCount={2}
                boundaryCount={0}
                renderItem={(item) => (
                    <PaginationItem
                        {...item}
                        sx={{
                            color: '#B75332',
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: '#B75332', color: '#ffffff' },
                            '&.Mui-selected': {
                                backgroundColor: '#B75332',
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#B75332', color: '#ffffff' },
                            },
                            '&:not(:last-of-type)': { borderRight: 'none' },
                            '& .MuiPaginationItem-ellipsis': { border: 'none' },
                            '&.MuiPaginationItem-previousNext': {
                                border: '0.5px solid black',
                                margin: isMobile ? responsiveTabMargin : '0px -6px 0px -5.5px',
                            },
                        }}
                    />
                )}
                sx={{
                    display: 'flex',
                    flexWrap: isMobile ? 'nowrap' : 'wrap',
                    '& .MuiPagination-ul': { flexWrap: isMobile ? 'nowrap' : 'wrap' },
                    '& .MuiPaginationItem-ellipsis': {
                        fontSize: '1rem',
                        color: 'black',
                        padding: 0,
                        margin: isMobile ? responsiveTabMargin : '0px ',
                    },
                    '.MuiPaginationItem-page': {
                        margin: isMobile ? responsiveTabMargin : '0px -5px 0px -5px',
                        border: '0.5px solid black',
                        borderRadius: 0.5,
                        color: '#B75332',
                        '&:hover': { backgroundColor: '#B75332', color: '#ffffff' },
                        '&.Mui-selected': {
                            backgroundColor: '#B75332',
                            border: '0.5px solid black',
                            color: '#ffffff',
                            '&:hover': { backgroundColor: '#B75332', color: '#ffffff' },
                        },
                    },
                }}
            />
        </div>
    );
};

export default CustomPagination;
